// Emails the patient a link to pay for a document (prescription / fit note / referral) after a consultation.
// Shared by the order page and the Failed payments page so both send exactly the same link and email.
import pusherService from '@/services/PusherService';

const PAID_STATUSES = ['CAPTURED', 'COUPON', 'GBB-COUPON', 'GB COUPON', 'GB-COUPON', 'PRE-AUTHORIZED'];

const DOC_TIMES = {
  prescription: ['prescriptionProposedTime', 'prescriptionCreatedTime'],
  fitnote: ['fitNoteProposedTime', 'fitNoteCreatedTime'],
  referral: ['referralLetterProposedTime', 'referralLetterCreatedTime']
};

export const DOC_LABELS = {
  prescription: 'Prescription',
  fitnote: 'Fit note',
  referral: 'Referral letter'
};

export function isDocumentPaid (status) {
  return PAID_STATUSES.includes(status);
}

/** Documents the clinician proposed or created, from the latest consultation log row. */
export function docTypesFromLog (log) {
  if (!log) return [];
  return Object.keys(DOC_TIMES)
    .filter((key) => DOC_TIMES[key].some((field) => log[field]))
    .map((key) => ({ key, value: DOC_LABELS[key] }));
}

function emailLabel (docType) {
  const key = (docType || '').toLowerCase();
  if (key.includes('fit')) return 'Fit note (sick note)';
  if (key.includes('referral')) return 'Referral letter';
  return 'Private prescription';
}

/** Same rule as the order page: physical prescriptions also charge medication and delivery. */
export function totalCharge (docType, prescriptionType, fees) {
  const doc = parseFloat(fees.documentFee) || 0;
  if (docType === 'prescription' && prescriptionType === 'PHYSICAL') {
    return doc + (parseFloat(fees.medicationFee) || 0) + (parseFloat(fees.deliveryFee) || 0);
  }
  return doc;
}

/** Waits for the one-time token the payment page needs (pushed over Pusher after we ask for it). */
async function preAuthToken (orderService, email, orderId) {
  let unsubscribe = null;
  const token = new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (unsubscribe) unsubscribe();
      reject(new Error('Timed out waiting for the payment link token'));
    }, 10000);
    unsubscribe = pusherService.listen('pre-auth-token', 'token', (data) => {
      clearTimeout(timer);
      unsubscribe();
      resolve(data && data.preAuthToken ? data.preAuthToken : data);
    });
  });
  await orderService.getPreAuthToken(email, orderId);
  return token;
}

/**
 * Sends the document payment link email.
 * @param orderService this.$orderService
 * @param opts { orderId, patientId, email, docType, prescriptionType }
 */
export async function sendDocumentPaymentLink (orderService, opts) {
  const { orderId, patientId, email, docType, prescriptionType } = opts;
  if (!email) throw new Error('This patient has no email address');
  if (!docType) throw new Error('Choose which document the patient is paying for');

  const token = await preAuthToken(orderService, email, orderId);

  const fees = { documentFee: 0, medicationFee: 0, deliveryFee: 0 };
  try {
    const status = await orderService.getAdditionalPaymentStatus(orderId);
    const data = (status && status.data) || {};
    fees.documentFee = data.documentFee || 0;
    fees.medicationFee = data.medicationPaymentPrice || 0;
    fees.deliveryFee = data.deliveryFee || 0;
  } catch (e) {
    // Fees are only shown in the email; the payment page works them out itself.
  }

  const label = emailLabel(docType);
  const key = docType.toLowerCase();
  const payload = {
    message: `Your clinician has approved a ${label.toLowerCase()} after your consultation (order #${orderId}). Pay using the link below so we can issue it.`,
    emailTemplateName: 'prescription-order-summary-payment.html',
    whatsAppTemplate: '',
    subject: `Action required: pay for your ${label.toLowerCase()} — order #${orderId}`,
    isSmsEnable: false,
    isWhatsAppEnable: false,
    isEmailEnable: true,
    type: 'DOCUMENT-PAYMENT-REQUEST',
    heading: `Complete payment for your ${label}`,
    url1: `${process.env.VUE_APP_PATIENT_URL}document-payment/${orderId}/${patientId}/${key}/${token}`,
    orderItemId: orderId,
    documentType: label,
    deliveryMethod: prescriptionType,
    additionalServiceFee: fees.documentFee,
    medicationFee: fees.medicationFee,
    deliveryFee: fees.deliveryFee,
    totalCharge: totalCharge(key, prescriptionType, fees),
    addressType: 'DELIVERY'
  };

  const res = await orderService.resendDocumentConsent(payload);
  if (!res || res.status !== 'OK') {
    throw new Error((res && res.message) || 'The email service did not accept the request');
  }
  return res;
}
